import useSWR from "swr";
import { officeSourceId, officeSourcePage } from "./office-source";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  DocumentPage,
  DocumentSource,
  DocumentVersion,
} from "src/types/federation-document";

export const documentBase = (federation: number) =>
  `/providers/${federation}/federation`;
export function useDocumentData<T>(
  federation: number,
  path: string,
  enabled = true,
  params: Record<string, unknown> = {},
) {
  const { user } = useAuthContext();
  const source = path === "movements" ? officeSourceId("transfers", federation) : 0;
  const selectedPath = source ? `${path}/${source}` : path;
  return useSWR<T>(
    enabled && federation > 0 && user?.id
      ? [`${documentBase(federation)}/${selectedPath}#actor-${user.id}`, { params }]
      : null,
    source ? officeSourcePage : fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function useProcessDocuments(
  federation: number,
  source: DocumentSource,
  sourceId: number,
  workspace?: number,
) {
  return useDocumentData<DocumentPage>(
    federation,
    "process-documents",
    sourceId > 0,
    {
      source_type: source,
      source_id: sourceId,
      workspace_provider_id: workspace,
    },
  );
}
export async function documentCommand<T>(
  federation: number,
  path: string,
  payload: Record<string, unknown>,
  workspace?: number,
) {
  return (
    await axios.post<T>(`${documentBase(federation)}/${path}`, payload, {
      params: { workspace_provider_id: workspace },
    })
  ).data;
}
export async function uploadProcessDocument(
  federation: number,
  command: Record<string, unknown>,
  file: File,
  workspace?: number,
) {
  const body = new FormData();
  body.append("payload", JSON.stringify(command));
  body.append("file", file);
  return (
    await axios.post<DocumentPage>(
      `${documentBase(federation)}/process-documents`,
      body,
      {
        params: { workspace_provider_id: workspace },
      },
    )
  ).data;
}
export async function processDocumentContent(
  federation: number,
  version: DocumentVersion,
  workspace?: number,
) {
  const url = version.id
    ? `${documentBase(federation)}/process-document-versions/${
        version.id
      }/content`
    : `/providers/federation-player-documents/${version.registration_document_id}/content`;
  return (
    await axios.get<Blob>(url, {
      responseType: "blob",
      params: { workspace_provider_id: workspace },
    })
  ).data;
}
export async function generatedDocumentContent(
  federation: number,
  identity: number,
  workspace?: number,
) {
  return (
    await axios.get<Blob>(
      `${documentBase(federation)}/generated-documents/${identity}/content`,
      {
        responseType: "blob",
        params: { workspace_provider_id: workspace },
      },
    )
  ).data;
}
export async function documentTemplatePreview(
  federation: number,
  identity: number,
) {
  return (
    await axios.get<Blob>(
      `${documentBase(federation)}/document-templates/${identity}/preview`,
      { responseType: "blob" },
    )
  ).data;
}
export function useDocumentCapabilities(federation: number, enabled: boolean) {
  return useDocumentData<{ allowed_actions: string[] }>(
    federation,
    "document-capabilities",
    enabled,
  );
}
export function useOwnDocumentFederations() {
  const { user } = useAuthContext();
  return useSWR<{ items: { id: number; player_id: number; label: string }[] }>(
    user?.id
      ? `/providers/federation-documents/my-federations#actor-${user.id}`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function documentError(error: unknown) {
  const failure = error as {
    detail?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  const detail = failure?.response?.data?.detail ?? failure?.detail;
  const status = failure?.response?.status;
  if (typeof detail === "string") {
    if (/setup|capability_unavailable|decision_unavailable/.test(detail))
      return "fd_setup";
    if (/file_size|too_large/.test(detail)) return "fd_file_size";
    if (/file_type|file_invalid|document_invalid|profile_image/.test(detail))
      return "fd_file_invalid";
    if (/information_or_review_conflict/.test(detail))
      return "fd_information_conflict";
    if (/conflict|version_not_current/.test(detail)) return "fd_conflict";
    if (/template_unavailable|type_unavailable/.test(detail))
      return "fd_unavailable";
    if (/approved_photo_required/.test(detail)) return "fd_photo_required";
    if (/licence_not_valid|approved_player_required/.test(detail))
      return "fd_issuance_blocked";
    if (/source_closed/.test(detail)) return "fd_closed";
    if (
      /permission_denied|not_found|verified_account_required|self_review/.test(
        detail,
      )
    )
      return "fd_denied";
    if (/invalid|required|unsupported|mismatch|privacy_downgrade/.test(detail))
      return "fd_invalid";
  }
  if (Array.isArray(detail) || status === 422) return "fd_invalid";
  if ([401, 403, 404].includes(status || 0)) return "fd_denied";
  if (status === 409) return "fd_conflict";
  if (status === 503) return "fd_setup";
  return "fd_error";
}
